import { calcularFactura } from "@/lib/billing/factura-de-modulos"
import { MODULOS_VENDIBLES, TARIFA_VIGENTE, nucleoDe, precioDe, type ModuloVendible } from "@/lib/billing/precios"
import { CUANTOS_POR_AREA, porcentajeDelPack } from "@/lib/billing/packs-de-area"
import type { AreaDeModulo } from "@/lib/modules/areas"
import { AREAS_EN_ORDEN } from "./catalogo"
import { eurosEn, type Idioma } from "./idioma"

/**
 * El precio que enseña la web, con la misma cuenta que se cobra.
 *
 * No hay otra aritmética: `calcularFactura` es la que usa la suscripción y la
 * que se le manda a Stripe. Aquí solo se agrupa por área para pintarlo.
 */

export interface LineaDeArea {
  area: AreaDeModulo
  modulos: number
  /** Si alguno de sus módulos se paga por persona. */
  porPersona: boolean
  /** Lo que paga el área, con el pack ya descontado, en céntimos. */
  centimos: number
  pack: number
}

export interface PrecioDeLanding {
  totalCentimos: number
  /** Lo que costaría sin packs: para enseñar el ahorro. */
  brutoCentimos: number
  lineas: LineaDeArea[]
}

export function precioMensual(modulos: readonly ModuloVendible[], empleados: number): PrecioDeLanding {
  // La web enseña siempre la tarifa de las altas nuevas.
  const factura = calcularFactura({ modulos, empleados, intervalo: "mensual", tarifa: TARIFA_VIGENTE })
  const lineas: LineaDeArea[] = []
  for (const area of AREAS_EN_ORDEN) {
    const deArea = factura.lineas.filter((l) => l.area === area)
    if (deArea.length === 0) continue
    const base = deArea.reduce((s, l) => s + l.centimosLinea, 0)
    const descuento = factura.descuentos.find((d) => d.area === area)
    lineas.push({
      area,
      modulos: deArea.length,
      porPersona: deArea.some((l) => l.concepto !== "nucleo" && precioDe(l.concepto, TARIFA_VIGENTE).tipo === "por_empleado"),
      centimos: base - (descuento?.centimos ?? 0),
      pack: descuento?.porcentaje ?? 0,
    })
  }
  const brutoCentimos = factura.lineas.reduce((s, l) => s + l.centimosLinea, 0)
  return { totalCentimos: factura.totalCentimos, brutoCentimos, lineas }
}

/** Cuántos le faltan a un área para el siguiente pack, o null si ya tiene el máximo. */
export function siguientePack(area: AreaDeModulo, encendidos: number): { faltan: number; porcentaje: number } | null {
  const total = CUANTOS_POR_AREA[area]
  if (porcentajeDelPack(encendidos, total) === 30) return null
  if (encendidos >= 3) return { faltan: total - encendidos, porcentaje: 30 }
  return { faltan: 3 - encendidos, porcentaje: 20 }
}

export function areaDe(modulo: ModuloVendible): AreaDeModulo {
  return precioDe(modulo, TARIFA_VIGENTE).area
}

/** 6050 → «60,50 €» (o «€60.50» en inglés). */
export function euros(centimos: number, idioma: Idioma = "es"): string {
  return eurosEn(idioma, centimos)
}

/** El precio unitario de un módulo, como se lee en la web. */
export function precioUnitario(modulo: ModuloVendible, idioma: Idioma = "es"): string {
  const p = precioDe(modulo, TARIFA_VIGENTE)
  const [persona, mes] = idioma === "en" ? ["person", "month"] : ["persona", "mes"]
  return p.tipo === "por_empleado"
    ? `${euros(p.centimosMensuales, idioma)} / ${persona} / ${mes}`
    : `${euros(p.centimosMensuales, idioma)} / ${mes}`
}

/** «19 €»: la cuota del núcleo, sin decimales si no los tiene. */
export function eurosRedondos(centimos: number, idioma: Idioma = "es"): string {
  return eurosEn(idioma, centimos, true)
}

export const NUCLEO_CENTIMOS = nucleoDe(TARIFA_VIGENTE)
export const NUCLEO = eurosRedondos(NUCLEO_CENTIMOS)

/** La cuota del núcleo en un idioma: «19 €» o «€19». */
export function nucleoTexto(idioma: Idioma = "es"): string {
  return eurosRedondos(NUCLEO_CENTIMOS, idioma)
}

/**
 * Lo que cuesta un módulo de un área, en corto. Dirección mezcla: Informes es
 * fijo y Chat y Proyectos van por persona.
 */
export function unidadDeArea(area: AreaDeModulo, idioma: Idioma = "es"): string {
  const deArea = MODULOS_VENDIBLES.map((m) => precioDe(m, TARIFA_VIGENTE)).filter((p) => p.area === area)
  const plano = deArea.find((p) => p.tipo === "plano")
  const porPersona = deArea.find((p) => p.tipo === "por_empleado")
  const [mes, persona, o] = idioma === "en" ? ["month", "person", " or "] : ["mes", "persona", " o "]
  return [
    plano && `${eurosRedondos(plano.centimosMensuales, idioma)} / ${mes}`,
    porPersona && `${euros(porPersona.centimosMensuales, idioma)} / ${persona}`,
  ]
    .filter(Boolean)
    .join(o)
}
